// Bridges fabric objects to the pure region math in ./region.
//
// Nothing here imports fabric - objects are duck-typed - so this stays cheap to
// reason about and the geometry underneath remains unit testable on its own.

import {
	isPolygonCovered,
	polygonBounds,
	maxScaleThatFits,
	candidateCenters,
	largestRectOfComponent,
	translatePolygon
} from './region';

// Four true rotated corners of an object, in canvas coordinates.
//
// Deliberately NOT getBoundingRect(): that returns the axis-aligned box around
// a rotated object, which is far too conservative for rotated text spanning a
// region. `calculate: true` recomputes live - fabric only refreshes its cached
// coords when a transform ends, so during a drag the cache is a frame stale.
export function objectPolygon(obj, opts) {
	const inset = (opts && opts.inset) || 0;
	let pts;

	if (typeof obj.getCoords === 'function') {
		try {
			pts = obj.getCoords(true, true).map(p => ({ x: p.x, y: p.y }));
		} catch (err) {
			pts = null;
		}
	}
	if (!pts || pts.length !== 4) pts = fallbackCorners(obj);
	if (inset > 0) pts = dilateQuad(pts, inset);
	return pts;
}

function fallbackCorners(obj) {
	const w = obj.getScaledWidth ? obj.getScaledWidth() : (obj.width * (obj.scaleX || 1));
	const h = obj.getScaledHeight ? obj.getScaledHeight() : (obj.height * (obj.scaleY || 1));
	const rad = ((obj.angle || 0) * Math.PI) / 180;
	const cos = Math.cos(rad);
	const sin = Math.sin(rad);
	// obj.left/top are the unrotated top-left when originX/Y are left/top.
	const cx = obj.left + (w * cos - h * sin) / 2;
	const cy = obj.top + (w * sin + h * cos) / 2;
	return [
		[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]
	].map(([dx, dy]) => ({
		x: cx + dx * cos - dy * sin,
		y: cy + dx * sin + dy * cos
	}));
}

// Grow the quad by `d` along its own axes. Used only when a safety margin is
// wanted: growing the query is safe, whereas shrinking the region would invent
// fake gaps at exactly the seams elements need to span across.
function dilateQuad(pts, d) {
	const [tl, tr, br, bl] = pts;
	const u = unit(tr.x - tl.x, tr.y - tl.y);
	const v = unit(bl.x - tl.x, bl.y - tl.y);
	return [
		{ x: tl.x - (u.x + v.x) * d, y: tl.y - (u.y + v.y) * d },
		{ x: tr.x + (u.x - v.x) * d, y: tr.y + (u.y - v.y) * d },
		{ x: br.x + (u.x + v.x) * d, y: br.y + (u.y + v.y) * d },
		{ x: bl.x - (u.x - v.x) * d, y: bl.y - (u.y - v.y) * d }
	];
}

function unit(x, y) {
	const m = Math.hypot(x, y) || 1;
	return { x: x / m, y: y / m };
}

export function isObjectCovered(region, obj, opts) {
	return isPolygonCovered(region, objectPolygon(obj, opts));
}

// The point fabric holds fixed for the duration of a scale drag. Falls back to
// the object centre - text/tool.js re-fires object:scaling with no transform.
export function transformAnchor(obj, transform) {
	const originX = (transform && transform.originX) || 'center';
	const originY = (transform && transform.originY) || 'center';
	let p;
	try {
		p = typeof obj.getPointByOrigin === 'function'
			? obj.getPointByOrigin(originX, originY)
			: obj.getCenterPoint();
	} catch (err) {
		p = obj.getCenterPoint();
	}
	return { x: p.x, y: p.y, originX, originY };
}

export function capturePose(obj) {
	return {
		left: obj.left,
		top: obj.top,
		scaleX: obj.scaleX,
		scaleY: obj.scaleY,
		angle: obj.angle,
		fontSize: obj.fontSize
	};
}

export function applyPose(obj, pose) {
	if (!pose) return false;
	obj.set({ left: pose.left, top: pose.top, scaleX: pose.scaleX, scaleY: pose.scaleY, angle: pose.angle });
	if (pose.fontSize != null && obj.type === 'text') obj.set({ fontSize: pose.fontSize });
	obj.setCoords();
	return true;
}

// The smallest scale an object may ever hold. Zero collapses it to nothing and
// a negative scale mirrors it - both are states the user cannot drag back out
// of, since every handle sits on top of the anchor once the object has no size.
export const MIN_SCALE = 0.01;

// Never let a scale reach zero or cross it. A negative value only arises from a
// drag that ran past the anchor, and the object the user meant to shrink is the
// unmirrored one, so fold the sign away rather than keeping the flip.
export function clampScale(scale) {
	const s = Math.abs(scale || 0);
	return s < MIN_SCALE ? MIN_SCALE : s;
}

function setScaleAboutAnchor(obj, factor, anchor) {
	obj.scaleX = clampScale((obj.scaleX || 1) * factor);
	obj.scaleY = clampScale((obj.scaleY || 1) * factor);
	obj.setPositionByOrigin({ x: anchor.x, y: anchor.y }, anchor.originX || 'center', anchor.originY || 'center');
	obj.setCoords();
}

// Cap an in-progress scale so the object never leaves the region.
//
// Replaces the old per-edge overflow test that backed off by a fixed 0.05 per
// frame: capping continuously gives smooth resistance at the boundary instead
// of visible stepping, and needs no lockScalingX/Y toggling.
export function capObjectScale(region, obj, anchor, minFactor) {
	if (!region || region.isEmpty) return { clamped: false, factor: 1 };

	const pts = objectPolygon(obj);
	if (isPolygonCovered(region, pts)) return { clamped: false, factor: 1 };

	// A caller that derives minFactor from the previous frame can hand us a
	// negative floor when the object arrived here already mirrored; a negative
	// factor would flip it again rather than shrink it.
	const lo = Math.max(0, minFactor != null ? minFactor : 0);
	const factor = maxScaleThatFits(region, pts, anchor, { lo, hi: 1 });
	setScaleAboutAnchor(obj, factor, anchor);
	return { clamped: true, factor };
}

// Shrink an object about `anchor` until it fits. Returns true if it resized.
export function fitObjectIntoRegion(region, obj, anchor) {
	if (!region || region.isEmpty) return false;

	const a = anchor || (() => { const c = obj.getCenterPoint(); return { x: c.x, y: c.y }; })();
	const pts = objectPolygon(obj);
	if (isPolygonCovered(region, pts)) return false;

	const factor = maxScaleThatFits(region, pts, a, { lo: 0, hi: 1 });
	if (!isFinite(factor) || factor <= 0 || factor >= 1) return false;
	setScaleAboutAnchor(obj, factor, { x: a.x, y: a.y, originX: 'center', originY: 'center' });
	return true;
}

function moveCenterTo(obj, x, y) {
	obj.setPositionByOrigin({ x, y }, 'center', 'center');
	obj.setCoords();
}

// Bring an object that ended up outside the region back in, disturbing it as
// little as possible.
export function snapObjectIntoRegion(region, obj, lastValidPose) {
	if (!region || region.isEmpty) return false;
	if (isObjectCovered(region, obj)) return false;

	// 1. The last pose the user actually dragged through that was legal. Exact,
	//    adjacent to the cursor, and resolves nearly every real case.
	if (lastValidPose) {
		applyPose(obj, lastValidPose);
		if (isObjectCovered(region, obj)) return true;
	}

	// 2. Nearest centre that keeps the object inside one piece of the region.
	const pts = objectPolygon(obj);
	const b = polygonBounds(pts);
	const cx = (b.x0 + b.x1) / 2;
	const cy = (b.y0 + b.y1) / 2;
	const cands = candidateCenters(region, pts);
	const probe = [];
	for (const c of cands) {
		if (isPolygonCovered(region, translatePolygon(pts, c.x - cx, c.y - cy, probe))) {
			moveCenterTo(obj, c.x, c.y);
			return true;
		}
	}

	// 3. Nothing fits as-is: move to the best candidate and shrink to fit.
	const target = cands[0] || centreOfLargestPiece(region);
	if (target) {
		moveCenterTo(obj, target.x, target.y);
		fitObjectIntoRegion(region, obj, { x: target.x, y: target.y });
		if (isObjectCovered(region, obj)) return true;
	}

	// 4. Last resort - the biggest piece of the region always admits a small
	//    enough object.
	const fallback = centreOfLargestPiece(region);
	if (fallback) {
		moveCenterTo(obj, fallback.x, fallback.y);
		fitObjectIntoRegion(region, obj, fallback);
		return true;
	}
	return false;
}

function centreOfLargestPiece(region) {
	let best = null;
	let bestArea = -1;
	for (let c = 0; c < region.componentCount; c++) {
		const r = largestRectOfComponent(region, c);
		if (!r) continue;
		const area = (r.x1 - r.x0) * (r.y1 - r.y0);
		if (area > bestArea) { bestArea = area; best = r; }
	}
	return best ? { x: (best.x0 + best.x1) / 2, y: (best.y0 + best.y1) / 2 } : null;
}

// The smallest font size text is ever auto-shrunk to. Below this the text is
// unreadable in print, so instead of shrinking further the caller locks the
// input - the customer has to shorten the text or use a bigger print area.
export const MIN_FONT_SIZE = 5;

function setFontSizeAbout(obj, fontSize, center) {
	obj.set({ fontSize, scaleX: 1, scaleY: 1 });
	obj.setPositionByOrigin(center, 'center', 'center');
	obj.setCoords();
}

// Bake a text object's scale into its fontSize, then make sure the baked result
// is still covered: Math.round can round up and glyph advances are not linear
// in fontSize, so the result can exceed the scale that was validated mid-drag.
//
// The same trim is what makes typing past the edge of the print area shrink the
// text instead of stopping it: growing text is refitted here down to
// MIN_FONT_SIZE. Returns true for any text object - ask isObjectCovered whether
// the result actually fits, since it will not once the floor is reached.
export function bakeTextScale(region, obj, onBaked) {
	if (obj.type !== 'text') return false;

	const center = obj.getCenterPoint();
	// clampScale keeps a mirrored or collapsed scale from baking into a zero or
	// negative fontSize, which fabric renders as an invisible, unselectable text.
	const baked = Math.max(MIN_FONT_SIZE, Math.round(obj.fontSize * clampScale(obj.scaleX)));
	setFontSizeAbout(obj, baked, center);

	// Largest font size that still fits, by bisection over the integer sizes.
	// Coverage is monotone in fontSize about a fixed centre, exactly as it is in
	// scale, so this lands on the same answer the old step-by-one loop did - but
	// in ~log2(size) tests, so a 200pt heading trimmed to the floor no longer
	// runs out of iterations halfway and leaves overflowing text on the canvas.
	if (region && !region.isEmpty && !isObjectCovered(region, obj)) {
		let lo = MIN_FONT_SIZE;  // candidate, not yet known to fit
		let hi = baked;          // known not to fit
		setFontSizeAbout(obj, lo, center);
		if (isObjectCovered(region, obj)) {
			while (hi - lo > 1) {
				const mid = Math.floor((lo + hi) / 2);
				setFontSizeAbout(obj, mid, center);
				if (isObjectCovered(region, obj)) lo = mid; else hi = mid;
			}
			setFontSizeAbout(obj, lo, center);
		}
		// Nothing fits, not even the floor: leave it there and let the caller
		// lock. Reporting a smaller size would be a size we refuse to print.
	}

	if (typeof onBaked === 'function') onBaked(obj);
	return true;
}

// --- the invariant ---------------------------------------------------------

// How many snap/bake rounds an element gets. Two settle every case below; the
// cap only stops a pathological region from spinning.
export const ENFORCE_PASSES = 4;

// Every gesture and every edit ends here. Whatever happened while it was
// running - a resize dragged past the edge, a font size typed in the parent, a
// rotation - this is what guarantees the element is back inside the region by
// the time the action is over.
//
// Neither half can do it alone, and running them once each in either order is
// not enough:
//
//   - snapObjectIntoRegion positions the element and, only if it has to,
//     shrinks it. On text the shrink lands in scaleX, which is not where text
//     carries its size.
//   - bakeTextScale moves that scale into fontSize about a *fixed centre*, and
//     the baked size can come out larger than the scale that was just
//     validated: Math.round rounds up, glyph advances are not linear in
//     fontSize, and the result is floored at MIN_FONT_SIZE. That floor is the
//     big one - text dragged below 5pt is baked back up to 5pt, several times
//     the size that was on the canvas. Growing about the centre pushes the text
//     out of whichever edge it was sitting against, and the only answer
//     bakeTextScale has to overflow is to shrink further, which at the floor it
//     cannot do. So it left the text hanging outside and reported "locked".
//
// Running them in a loop closes that: the snap after a bake simply moves the
// text back in, at a size the print area can hold. It converges because a bake
// leaves the text at scale 1, so the next bake is a no-op unless the snap
// shrank it again - and a snap that shrinks strictly reduces the size.
//
// Returns whether the element ended up covered. False means the region cannot
// hold it at all (text at MIN_FONT_SIZE in a print area narrower than the
// word), which is what the caller turns into the locked flag.
export function enforceObjectInRegion(region, obj, opts) {
	const o = opts || {};
	if (!obj) return false;

	// No boxes means no constraint - but text still has to end up at scale 1,
	// because the size reported to the parent is read off fontSize alone.
	if (!region || region.isEmpty) {
		if (o.bakeText !== false) bakeTextScale(region, obj, o.onBaked);
		return true;
	}

	// Only the first pass may fall back on the pose the gesture started from.
	// After that the element has already been moved, and the pose is stale.
	let pose = o.lastValidPose || null;

	for (let pass = 0; pass < ENFORCE_PASSES; pass++) {
		snapObjectIntoRegion(region, obj, pose);
		pose = null;
		if (o.bakeText === false) break;
		// Not text: the snap is the whole story, and it either worked or the
		// region is too small for the element.
		if (!bakeTextScale(region, obj, o.onBaked)) break;
		if (isObjectCovered(region, obj)) break;
	}

	return isObjectCovered(region, obj);
}

// --- box helpers (boxes stay the unit the UI talks about) ------------------

// The single surviving copy of the "which box is this point in" lookup, which
// used to be duplicated at four call sites. Only drives which box receives the
// next inserted element - it no longer constrains anything.
export function boxAtPoint(boxes, x, y) {
	if (!boxes || boxes.length === 0) return null;
	return boxes.find(b =>
		x >= b.left && x <= b.left + b.width &&
		y >= b.top && y <= b.top + b.height
	) || null;
}

export function defaultBoxFor(boxes) {
	if (!boxes || boxes.length === 0) return null;
	return boxes.find(b => b.default === true)
		|| boxes.reduce((a, b) => (b.width * b.height > a.width * a.height ? b : a), boxes[0]);
}

// Centre of an object's current visual bounds, without going through fabric.
export function objectCenter(obj) {
	const b = polygonBounds(objectPolygon(obj));
	return { x: (b.x0 + b.x1) / 2, y: (b.y0 + b.y1) / 2 };
}
